import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { ACTION_TYPE_DEFINITIONS } from '@/constants/actions'
import type { ActionType, ActionStatus } from '@/types'

interface ActionHistoryFiltersProps {
  actionType: ActionType | undefined
  status: ActionStatus | undefined
  tickFrom: number | undefined
  tickTo: number | undefined
  onActionTypeChange: (type: ActionType | undefined) => void
  onStatusChange: (status: ActionStatus | undefined) => void
  onTickRangeChange: (from?: number, to?: number) => void
}

const STATUS_OPTIONS: { value: ActionStatus; label: string }[] = [
  { value: 'queued', label: 'Queued' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'cancelled', label: 'Cancelled' },
]

export function ActionHistoryFilters({
  actionType,
  status,
  tickFrom,
  tickTo,
  onActionTypeChange,
  onStatusChange,
  onTickRangeChange,
}: ActionHistoryFiltersProps) {
  const hasFilters = actionType || status || tickFrom !== undefined || tickTo !== undefined

  const handleClearAll = () => {
    onActionTypeChange(undefined)
    onStatusChange(undefined)
    onTickRangeChange(undefined, undefined)
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {/* Action Type Filter */}
        <Select
          value={actionType ?? '_all'}
          onValueChange={(v) => onActionTypeChange(v === '_all' ? undefined : (v as ActionType))}
        >
          <SelectTrigger size="sm" className="w-[140px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Types</SelectItem>
            {ACTION_TYPE_DEFINITIONS.map((def) => (
              <SelectItem key={def.type} value={def.type}>
                {def.icon} {def.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select
          value={status ?? '_all'}
          onValueChange={(v) => onStatusChange(v === '_all' ? undefined : (v as ActionStatus))}
        >
          <SelectTrigger size="sm" className="w-[130px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Status</SelectItem>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Tick Range */}
        <div className="flex items-center gap-1">
          <Input
            type="number"
            placeholder="From tick"
            className="w-[90px] h-8 text-xs"
            min={1}
            value={tickFrom ?? ''}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value, 10) : undefined
              onTickRangeChange(val, tickTo)
            }}
          />
          <span className="text-xs text-muted-foreground">-</span>
          <Input
            type="number"
            placeholder="To tick"
            className="w-[90px] h-8 text-xs"
            min={1}
            value={tickTo ?? ''}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value, 10) : undefined
              onTickRangeChange(tickFrom, val)
            }}
          />
        </div>

        {/* Clear Filters */}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearAll} className="text-xs">
            Clear filters
          </Button>
        )}
      </div>
    </div>
  )
}
